<script setup>
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import { useStore } from '@state/composables'
import { debounce } from '@utils/performance'
import MixTableBlock from '@components/mix-table-block.v2'
import ArtistBlock from '@components/artist/artist-block'
import ShowGridBlock from '@components/show/show-grid-block'
import GenreGridBlock from '@components/genre/genre-grid-block'
import libraryRecentSearch from '@services/library-recent-search-service'
import { useAnalytics } from '@composables/use-analytics'
import { searchLibrary } from '@utils/library-collections'

const props = defineProps({
    query: { type: String, default: '' },
})

const store = useStore()
const router = useRouter()
const analyticsService = useAnalytics()

const corpus = computed(() => ({
    mixes: store.getters['favorites/likedMixes'],
    artists: store.getters['favorites/followedArtists'],
    shows: store.getters['favorites/followedShows'],
    genres: store.getters['favorites/followedGenres'],
}))
const results = computed(() => searchLibrary(corpus.value, props.query))
// parents normally v-if this component away on an empty query — guard anyway
const hasQuery = computed(() => props.query.trim().length > 0)
// FR21: on a direct load with a query the store is empty until app-init hydrates —
// "no matches" is indistinguishable from "not loaded yet", so gate the "No results"
// empty state behind full hydration (partial results still render immediately)
const libraryLoaded = computed(() =>
    ['mixes', 'artists', 'shows', 'genres'].every(type => store.getters['favorites/isLoaded'](type))
)

// `debounce` has no cancel, so a pending timer survives unmount — and the destroyed
// component's computeds return their cached pre-clear values, which would track a
// search the user just cleared. Guard on the destroyed flag instead.
let destroyed = false
const trackSearch = debounce(() => {
    if (destroyed) return
    if (hasQuery.value) analyticsService.librarySearchPerformed(results.value.total)
}, 1000)

// immediate: inline parents mount this component ON the first keystroke, so the initial
// query arrives as the mount-time prop, not a change — without immediate it never tracks
watch(() => props.query, () => trackSearch(), { immediate: true })

// Recents are recorded at NAVIGATION time via a router hook — not via click handlers on the
// reused blocks (their internal links can't tell us which item was tapped).
const RESULT_ROUTES = { mix: 'mixes', artist: 'artists', show: 'shows', genre: 'genres' }

function rememberIfResult(to) {
    const type = RESULT_ROUTES[to.name]
    if (!type) return
    const match = results.value[type].find(item =>
        to.name === 'genre' ? item.slug === to.params.slug : String(item.id) === String(to.params.id)
    )
    if (!match) return
    libraryRecentSearch.addToRecent({
        type,
        id: match.id,
        title: match.title || match.name,
        name: to.name,
        params: to.params, // reopening a recent is a plain push — no slug/url reconstruction
    })
}

let unregisterRememberHook = null
onMounted(() => {
    unregisterRememberHook = router.beforeEach((to, from, next) => {
        rememberIfResult(to)
        next() // ALWAYS unconditionally — this hook must never block or redirect
    })
})
onBeforeUnmount(() => {
    destroyed = true
    unregisterRememberHook && unregisterRememberHook()
})
</script>

<template>
    <div v-if="hasQuery">
        <template v-if="results.total">
            <template v-if="results.artists.length">
                <h2 class="group-title">Artists</h2>
                <ArtistBlock :artists="results.artists" />
            </template>
            <template v-if="results.mixes.length">
                <h2 class="group-title">Mixes</h2>
                <MixTableBlock
                    :mixes="results.mixes"
                    genres
                />
            </template>
            <template v-if="results.shows.length">
                <h2 class="group-title">Shows</h2>
                <ShowGridBlock :shows="results.shows" />
            </template>
            <template v-if="results.genres.length">
                <h2 class="group-title">Genres</h2>
                <GenreGridBlock
                    :genres="results.genres"
                    :followed-genre-ids="results.genres.map(genre => genre.id)"
                    :has-options="false"
                />
            </template>
        </template>
        <BaseSpinner v-else-if="!libraryLoaded" />
        <BaseEmptyList
            v-else
            :error="{
                title: 'No results',
                paragraph: 'Try searching again or use a different keyword',
            }"
        />
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.group-title {
    @include sectionHeading;

    margin: $s-l 0 $s-s;
}
</style>
